import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X, Briefcase, Users, UserCircle2, Building2, FileSpreadsheet, ShieldAlert, Clock, Camera, Loader2, LayoutDashboard, Trello, ClipboardList, StickyNote, MapPin, Settings, CreditCard, Archive, FileSignature, FolderKanban, BookOpen, LayoutGrid, UsersRound, CornerDownLeft } from 'lucide-react';
import LabelChip from './LabelChip';
import { useAuth } from '../context/AuthContext';
import { searchTargetsForUser } from './nav/navConfig';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;
const RECENT_KEY = 'ht_recent_searches_v1';
const MAX_RECENT = 5;

// Command palette registry (Ctrl/Cmd+K) — pages & actions the search can
// jump to directly, filtered by role. Keywords widen fuzzy matching.
// Command palette registry (Sep 2026): every nav page + merged-page tab comes
// from navConfig (role-filtered); a few actions are kept explicitly.
const ACTION_COMMANDS = [
  { name: 'Connect QuickBooks', path: '/settings?section=quickbooks', icon: FileSpreadsheet, keywords: 'qbo intuit sync accounting', roles: ['admin', 'supervisor'] },
  { name: 'Open User Manual', path: '/user-manual', icon: BookOpen, keywords: 'help docs documentation guide how to' },
  { name: 'Clock In / Out', path: '/clock-in', icon: Clock, keywords: 'punch time shift start' },
];
const commandsForUser = (user) => [
  ...searchTargetsForUser(user).map((n) => ({ name: `Go to ${n.name}`, path: n.path, icon: n.icon, keywords: n.keywords || '' })),
  ...ACTION_COMMANDS,
];

const CATEGORY_META = {
  jobs:          { label: 'Jobs', icon: Briefcase, color: '#1E88E5' },
  staff:         { label: 'Staff', icon: Users, color: '#00C896' },
  teams:         { label: 'Teams', icon: UserCircle2, color: '#A78BFA' },
  customers:     { label: 'Customers', icon: Building2, color: '#FFA000' },
  time_entries:  { label: 'Time Entries', icon: Clock, color: '#06B6D4' },
  photos:        { label: 'Photos', icon: Camera, color: '#EC4899' },
  reports:       { label: 'Reports', icon: FileSpreadsheet, color: '#06B6D4' },
  announcements: { label: 'Announcements', icon: ShieldAlert, color: '#EF4444' },
};

function StatusBadge({ status }) {
  if (!status) return null;
  const s = (status || '').toLowerCase();
  const map = {
    completed: { bg: 'rgba(0,200,150,0.15)', color: '#00C896' },
    in_progress: { bg: 'rgba(30,136,229,0.15)', color: '#1E88E5' },
    pending: { bg: 'rgba(255,160,0,0.15)', color: '#FFA000' },
    paused: { bg: 'rgba(255,160,0,0.15)', color: '#FFA000' },
    cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444' },
  };
  const c = map[s] || { bg: '#1A2433', color: '#A0AEC0' };
  return <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide" style={{ background: c.bg, color: c.color }}>{s}</span>;
}

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT); }
  catch { return []; }
}
function saveRecent(q) {
  if (!q || q.trim().length < 2) return;
  const v = q.trim();
  const cur = loadRecent().filter((x) => x.toLowerCase() !== v.toLowerCase());
  cur.unshift(v);
  localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, MAX_RECENT)));
}
function clearRecent() { localStorage.removeItem(RECENT_KEY); }

// Helper to flatten ordered list of items for keyboard nav
function flattenResults(results) {
  if (!results) return [];
  const order = ['jobs', 'staff', 'teams', 'customers', 'time_entries', 'photos', 'reports', 'announcements'];
  const flat = [];
  for (const k of order) {
    (results[k] || []).slice(0, 5).forEach((item, idx) => {
      flat.push({ category: k, item, isFirstInCategory: idx === 0 });
    });
  }
  return flat;
}

export default function GlobalSearch({ token, role, isMobile = false, onCloseMobile }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(isMobile);
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [recent, setRecent] = useState(loadRecent());
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const isAdminLike = role === 'admin' || role === 'supervisor';

  // "/" and Ctrl/Cmd+K keyboard shortcuts to open the palette
  useEffect(() => {
    if (isMobile) return;
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTypingField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        return;
      }
      if (e.key === '/' && !isTypingField && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMobile]);

  // Click outside to close
  useEffect(() => {
    if (isMobile) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isMobile]);

  // Auto-focus when mobile overlay opens
  useEffect(() => {
    if (isMobile) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isMobile]);

  const runSearch = useCallback(async (q) => {
    // Workers get the page palette only — the data search API is manager-only.
    if (!q || q.trim().length < 2 || !isAdminLike) {
      setResults(null);
      setTotal(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/search`, {
        params: { q: q.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(res.data.results || {});
      setTotal(res.data.total || 0);
      setHighlight(-1);
    } catch (e) {
      console.error('Search failed', e);
      setResults({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults(null);
      setTotal(0);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const handleSelect = (url, q) => {
    saveRecent(q || query);
    setRecent(loadRecent());
    setOpen(false);
    setQuery('');
    setResults(null);
    if (isMobile) onCloseMobile?.();
    navigate(url);
  };

  const cmdQuery = query.trim().toLowerCase();
  const cmdMatches = cmdQuery.length >= 1
    ? commandsForUser(user).filter((c) =>
        (!c.roles || c.roles.includes(role)) &&
        `${c.name} ${c.keywords || ''}`.toLowerCase().includes(cmdQuery)
      ).slice(0, 8)
    : [];

  const flat = [
    ...cmdMatches.map((c) => ({ category: 'commands', item: { url: c.path, name: c.name } })),
    ...flattenResults(results),
  ];

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && flat[highlight]) {
        handleSelect(flat[highlight].item.url);
      } else if (flat.length > 0 && query.trim().length < 2) {
        // Only command matches exist (1-char queries) — jump to the first.
        handleSelect(flat[0].item.url);
      } else if (cmdMatches.length > 0) {
        // Command palette behaviour (Sep 2026): Enter runs the top "Go to…" command.
        handleSelect(cmdMatches[0].path);
      } else if (query.trim().length >= 2 && isAdminLike) {
        // navigate to full results page
        saveRecent(query);
        setRecent(loadRecent());
        setOpen(false);
        const q = query;
        setQuery('');
        setResults(null);
        if (isMobile) onCloseMobile?.();
        navigate(`/search?q=${encodeURIComponent(q)}`);
      }
    } else if (e.key === 'Escape' && isMobile) {
      onCloseMobile?.();
    }
  };


  const showDropdown = open && (query.length >= 1 || (recent.length > 0 && query.length === 0));

  return (
    <div ref={containerRef} className={isMobile ? 'fixed inset-0 z-[80]' : 'relative'} data-testid="global-search">
      {isMobile && (
        <div className="absolute inset-0" style={{ background: 'rgba(11,19,33,0.96)', backdropFilter: 'blur(8px)' }} onClick={onCloseMobile} />
      )}

      <div className={isMobile ? 'absolute top-0 left-0 right-0 p-3' : 'w-full'}>
        <div className={`relative flex items-center ${isMobile ? '' : ''}`} style={{
          background: '#0F1923',
          border: '1px solid #2D3748',
          borderRadius: 10,
        }}>
          <Search className="w-4 h-4 ml-3 flex-shrink-0" style={{ color: '#64748B' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={isAdminLike ? "Search or jump to — jobs, staff, customers, pages…" : "Jump to a page… (Ctrl K)"}
            className={`flex-1 min-w-0 bg-transparent text-white text-sm outline-none px-3 py-2 placeholder:text-slate-500`}
            style={{ caretColor: '#1E88E5' }}
            data-testid="global-search-input"
            aria-label="Global search"
          />
          {!isMobile && !query && (
            <span className="hidden lg:inline-flex items-center gap-1 mr-2 shrink-0">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono" style={{ background: '#1A2433', color: '#64748B' }}>
                {navigator.platform?.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'}
              </span>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono" style={{ background: '#1A2433', color: '#64748B' }}>/</span>
            </span>
          )}
          {(query || isMobile) && (
            <button
              onClick={() => {
                setQuery('');
                setResults(null);
                if (isMobile) onCloseMobile?.();
              }}
              className="p-2 rounded text-slate-500 hover:text-white"
              data-testid="global-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {showDropdown && (
          <div
            className="absolute left-0 right-0 mt-2 rounded-xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            data-testid="global-search-dropdown"
          >
            {/* Pages & actions (command palette) */}
            {cmdMatches.length > 0 && (
              <div className="border-b" style={{ borderColor: '#2D3748' }} data-testid="search-commands">
                <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <CornerDownLeft className="w-3.5 h-3.5" style={{ color: '#7BB8E8' }} />
                  <span className="text-xs uppercase font-bold tracking-wider" style={{ color: '#7BB8E8' }}>Pages & Actions</span>
                </div>
                {cmdMatches.map((c, idx) => {
                  const Icon = c.icon;
                  const isHighlighted = highlight === idx;
                  return (
                    <button
                      key={c.path + c.name}
                      onClick={() => handleSelect(c.path)}
                      onMouseEnter={() => setHighlight(idx)}
                      className="w-full text-left px-3 py-2 transition-colors flex items-center gap-2.5"
                      style={{ background: isHighlighted ? 'rgba(30,136,229,0.10)' : 'transparent' }}
                      data-testid={`search-command-${idx}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: '#64748B' }} />
                      <span className="text-sm text-white">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Searching…
              </div>
            )}

            {!loading && query.length === 0 && recent.length > 0 && (
              <div className="p-3" data-testid="recent-searches">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#64748B' }}>
                    <Clock className="inline w-3 h-3 mr-1" /> Recent searches
                  </p>
                  <button onClick={() => { clearRecent(); setRecent([]); }} className="text-xs text-slate-500 hover:text-white" data-testid="recent-clear">Clear</button>
                </div>
                {recent.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(r); runSearch(r); }}
                    className="w-full text-left text-sm py-1.5 px-2 rounded hover:bg-white/5 text-slate-300"
                    data-testid={`recent-item-${i}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}

            {!loading && results && total === 0 && query.length >= 2 && cmdMatches.length === 0 && (
              <div className="p-6 text-center text-sm" data-testid="search-empty" style={{ color: '#A0AEC0' }}>
                No results for <span className="text-white font-semibold">"{query}"</span>
              </div>
            )}

            {!loading && results && total > 0 && (
              <div>
                {Object.keys(CATEGORY_META).map((catKey) => {
                  const items = (results[catKey] || []).slice(0, 5);
                  if (items.length === 0) return null;
                  const meta = CATEGORY_META[catKey];
                  const Icon = meta.icon;
                  return (
                    <div key={catKey} className="border-b last:border-b-0" style={{ borderColor: '#2D3748' }} data-testid={`search-group-${catKey}`}>
                      <div className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(255,255,255,0.02)' }}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                          <span className="text-xs uppercase font-bold tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                        </div>
                        {(results[catKey] || []).length >= 5 && (
                          <button
                            onClick={() => {
                              const q = query;
                              saveRecent(q);
                              setRecent(loadRecent());
                              setOpen(false);
                              setQuery('');
                              setResults(null);
                              if (isMobile) onCloseMobile?.();
                              navigate(`/search?q=${encodeURIComponent(q)}&category=${catKey}`);
                            }}
                            className="text-xs hover:underline"
                            style={{ color: '#1E88E5' }}
                            data-testid={`see-all-${catKey}`}
                          >
                            See all
                          </button>
                        )}
                      </div>
                      <div>
                        {items.map((item, idx) => {
                          const flatIdx = flat.findIndex((f) => f.category === catKey && f.item === item);
                          const isHighlighted = flatIdx === highlight;
                          return (
                            <button
                              key={item.id || item.key || idx}
                              onClick={() => handleSelect(item.url)}
                              onMouseEnter={() => setHighlight(flatIdx)}
                              className="w-full text-left px-3 py-2.5 transition-colors block"
                              style={{ background: isHighlighted ? 'rgba(30,136,229,0.10)' : 'transparent' }}
                              data-testid={`search-result-${catKey}-${idx}`}
                            >
                              <ResultRow category={catKey} item={item} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {isAdminLike && <div className="px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <button
                    onClick={() => {
                      const q = query;
                      saveRecent(q);
                      setRecent(loadRecent());
                      setOpen(false);
                      setQuery('');
                      setResults(null);
                      if (isMobile) onCloseMobile?.();
                      navigate(`/search?q=${encodeURIComponent(q)}`);
                    }}
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#1E88E5' }}
                    data-testid="search-view-all"
                  >
                    View all {total} result{total !== 1 ? 's' : ''} →
                  </button>
                </div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultRow({ category, item }) {
  if (category === 'jobs') {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {item.job_number && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(30,136,229,0.15)', color: '#1E88E5' }}>{item.job_number}</span>}
            <span className="text-sm text-white truncate font-medium">{item.title}</span>
          </div>
          <p className="text-xs truncate mt-0.5" style={{ color: '#A0AEC0' }}>
            {item.customer_name || '—'}{item.worker_name ? ` · ${item.worker_name}` : ''}{item.address ? ` · ${item.address}` : ''}
          </p>
          {Array.isArray(item.labels) && item.labels.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1" data-testid="search-result-labels">
              {item.labels.slice(0, 3).map((l) => (
                <LabelChip key={l.id} label={l} size="sm" />
              ))}
              {item.labels.length > 3 && (
                <span className="text-[10px]" style={{ color: '#64748B' }}>
                  +{item.labels.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
        <StatusBadge status={item.status} />
      </div>
    );
  }
  if (category === 'staff') {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate font-medium">{item.name}</p>
          <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>
            {item.role}{item.team_name ? ` · ${item.team_name}` : ''}{item.email ? ` · ${item.email}` : ''}
          </p>
        </div>
        {item.clocked_in && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#00C896' }} /> ON CLOCK
          </span>
        )}
      </div>
    );
  }
  if (category === 'teams') {
    return (
      <div>
        <p className="text-sm text-white truncate font-medium">{item.name}</p>
        <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>
          {item.supervisor_name ? `Supervisor: ${item.supervisor_name}` : 'No supervisor'} · {item.member_count} member{item.member_count !== 1 ? 's' : ''}
        </p>
      </div>
    );
  }
  if (category === 'customers') {
    return (
      <div>
        <p className="text-sm text-white truncate font-medium">{item.name}</p>
        <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>
          {item.job_count} job{item.job_count !== 1 ? 's' : ''}{item.address ? ` · ${item.address}` : ''}
        </p>
      </div>
    );
  }
  if (category === 'reports') {
    return (
      <div>
        <p className="text-sm text-white truncate font-medium">{item.title}</p>
        <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>Open report</p>
      </div>
    );
  }
  if (category === 'time_entries') {
    const ci = item.clock_in ? new Date(item.clock_in) : null;
    const co = item.clock_out ? new Date(item.clock_out) : null;
    const dateStr = ci ? ci.toLocaleDateString() : '—';
    const ciStr = ci ? ci.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
    const coStr = co ? co.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'still on clock';
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate font-medium">{item.user_name}</p>
          <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>
            {dateStr} · {ciStr} → {coStr}{item.duration_hours != null ? ` · ${item.duration_hours}h` : ''}
          </p>
        </div>
        {item.active && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#00C896] bg-[#00C896]/15">ACTIVE</span>
        )}
      </div>
    );
  }
  if (category === 'photos') {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {item.job_number && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-[#1E88E5] bg-[#1E88E5]/15">{item.job_number}</span>}
            <span className="text-sm text-white truncate font-medium">{item.job_title}</span>
          </div>
          <p className="text-xs truncate mt-0.5" style={{ color: '#A0AEC0' }}>
            {item.customer_name || '—'} · {item.photo_count} photo{item.photo_count !== 1 ? 's' : ''}
          </p>
        </div>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#EC4899] bg-[#EC4899]/15">VIEW</span>
      </div>
    );
  }
  if (category === 'announcements') {
    return (
      <div>
        <p className="text-sm text-white truncate font-medium">{item.title}</p>
        <p className="text-xs truncate" style={{ color: '#A0AEC0' }}>
          {item.priority ? item.priority.toUpperCase() : 'INFO'}{item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString()}` : ''}
        </p>
      </div>
    );
  }
  return null;
}
